import type {
  ListNotificationsQuery,
  NotificationCounts,
  NotificationSummary,
  Paginated,
} from '@whn/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, toQueryString } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

function invalidate(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
}

export function useNotifications(params: Partial<ListNotificationsQuery> = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: ({ signal }) =>
      api.get<Paginated<NotificationSummary>>(
        `/notifications${toQueryString(params as Record<string, string | number>)}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * The badge count, polled.
 *
 * A minute is the right cadence for something that only decides whether a dot
 * appears: often enough that an approval request does not sit unnoticed for an
 * afternoon, rarely enough that an idle tab is not a load generator. A socket
 * would be the answer if notifications ever needed to be instant; they do not.
 */
export function useNotificationCounts() {
  return useQuery({
    queryKey: queryKeys.notifications.counts,
    queryFn: ({ signal }) => api.get<NotificationCounts>('/notifications/counts', signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<{ updated: number }>('/notifications/read', { ids }),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ updated: number }>('/notifications/read-all'),
    onSuccess: () => invalidate(queryClient),
  });
}
